'use client';

import { useHomeRecommendations } from '@hooks/features/useHomeRecommendations';
import { usePublicTopJobs } from '@hooks/features/usePublicTopJobs';
import { useEmployerTopJobs } from '@hooks/features/useEmployerTopJobs';


export type HomeJobsSlotState =
  | { variant: 'employer'; items: ApiSchema<'MyJobCard'>[]; isLoading: boolean; hasItems: boolean }
  | { variant: 'talent'; items: NonNullable<ApiSchema<'CursorJobResult'>['items']>; isLoading: boolean; hasItems: boolean };


/**
 * @description Single source of truth for the /mc (and /) home job slot.
 *
 * Resolves the visitor into one of two render variants:
 *  - `employer`: authenticated company account (`hasBusinessWallet`) WITH ≥1 own job → top-3 own jobs.
 *  - `talent`:   everyone else → public jobs (anon) or personalised recommendations (auth).
 *
 * All three sub-hooks are SWR-keyed, so calling this hook from multiple components
 * (the slot + its surrounding CTA gate) dedupes to one request per source.
 *
 * The employer/talent split keys off `isCompanyAccount` (authenticated + `hasBusinessWallet`),
 * NOT the MC context. Switching context only swaps the sidebar — the home slot stays put.
 */
export const useHomeJobsSlot = (
  locale: Locale,
  isAuthenticated: boolean,
  isCompanyAccount: boolean,
): HomeJobsSlotState => {
  const isCompany = isAuthenticated && isCompanyAccount;

  // Employer top jobs — only fetched in company context.
  const employer = useEmployerTopJobs(locale, isCompany);

  // The employer branch "wins" while it is still loading or once it has jobs.
  const showEmployer = isCompany && (employer.isLoading || employer.hasItems);

  // Public jobs — only for unauthenticated visitors.
  const publicJobs = usePublicTopJobs(locale, !isAuthenticated);

  // Recommendations — for authenticated users NOT showing the employer block
  // (candidate context, or company context resolved with no own jobs).
  const recommendations = useHomeRecommendations(locale, undefined, isAuthenticated && !showEmployer);

  if (showEmployer) {
    return {
      variant: 'employer',
      items: employer.items,
      isLoading: employer.isLoading,
      hasItems: employer.hasItems,
    };
  }

  const talent = isAuthenticated ? recommendations : publicJobs;

  return {
    variant: 'talent',
    items: talent.items,
    isLoading: talent.isLoading,
    hasItems: talent.hasItems,
  };
};
